import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "./AppContext";
import { ApiError, type SynthesisBatchList } from "./api";
import { useDeveloperMode } from "./uiMode";

export interface SynthesisBatchesState {
  batches: SynthesisBatchList | null;
  loading: boolean;
  problem: string | null;
  refresh: () => void;
}

const SynthesisBatchesContext = createContext<SynthesisBatchesState | null>(null);

/**
 * UI-24 — the processing view's one list read, held where the navigator panel and the index
 * page can both see it, the same shape `LedgerProvider` holds UI-9's one read in.
 *
 * Mounted inside the router rather than in `AppContext`, for the same two reasons
 * `LedgerProvider` is: the state belongs to one destination rather than the whole session,
 * and it fetches only while that destination is open — here, only while `/processing` is
 * open *and* developer mode is on, so turning developer mode off does not leave a poll
 * running for a rail button that no longer exists.
 *
 * Polls every 2s while any batch in the list has a run still in flight — the same interval
 * `useWorldViewActivity` uses for the single running batch its status line shows, applied
 * here to a whole page of them.
 */
export function SynthesisBatchesProvider({ children }: { children: React.ReactNode }) {
  const { api } = useApp();
  const developerMode = useDeveloperMode();
  const { pathname } = useLocation();

  const active = developerMode && pathname.startsWith("/processing");

  const [batches, setBatches] = useState<SynthesisBatchList | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  const load = useCallback(async () => {
    try {
      const next = await api.listSynthesisBatches();
      setBatches(next);
      setProblem(null);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The batch list could not be read.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    void load();
  }, [active, load, reloads]);

  const running = active && (batches?.batches.some((batch) => isInFlight(batch)) ?? false);
  const runningRef = useRef(running);
  runningRef.current = running;

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      if (runningRef.current) void load();
    }, 2000);
    return () => clearInterval(timer);
  }, [active, load]);

  const value: SynthesisBatchesState = {
    batches: active ? batches : null,
    loading: active ? loading : false,
    problem: active ? problem : null,
    refresh: () => setReloads((n) => n + 1),
  };

  return <SynthesisBatchesContext.Provider value={value}>{children}</SynthesisBatchesContext.Provider>;
}

function isInFlight(batch: SynthesisBatchList["batches"][number]): boolean {
  return batch.runStatus === "queued" || batch.runStatus === "processing";
}

/** Throws outside the provider, for the same reason `useApp`/`useLedger` do: a missing
 * provider is a wiring mistake that should fail loudly in development. */
export function useSynthesisBatches(): SynthesisBatchesState {
  const value = useContext(SynthesisBatchesContext);
  if (!value) throw new Error("useSynthesisBatches was called outside a SynthesisBatchesProvider.");

  return value;
}
